import React from 'react';
import { useMenuBuilder } from '../../context/MenuBuilderContext';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { MessageSquare, Link, RefreshCw, Trash2 } from 'lucide-react';
import { cn } from '../../lib/utils';

const actionTypes = [
  { value: 'message', label: 'Message', icon: MessageSquare },
  { value: 'uri', label: 'URI Link', icon: Link },
  { value: 'richmenuswitch', label: 'Menu Switch', icon: RefreshCw },
];

export const ActionEditor = ({ className }) => {
  const { menuData, selectedAreaIndex, updateArea, removeArea } = useMenuBuilder();

  const selectedArea = selectedAreaIndex !== null 
    ? menuData.areas[selectedAreaIndex] 
    : null;

  if (!selectedArea) {
    return (
      <div className={cn("glass-panel rounded-lg p-4", className)} data-testid="action-editor">
        <p className="label-text mb-3">Action Editor</p>
        <p className="text-[#52525B] text-sm">Select an area to edit its action</p>
      </div>
    );
  }

  const handleActionTypeChange = (type) => {
    const newAction = { type };
    
    if (type === 'message') {
      newAction.text = selectedArea.action.text || 'Hello';
    } else if (type === 'uri') {
      newAction.uri = selectedArea.action.uri || 'https://';
    } else if (type === 'richmenuswitch') {
      newAction.richMenuAliasId = selectedArea.action.richMenuAliasId || '';
      newAction.data = selectedArea.action.data || '';
    }
    
    updateArea(selectedAreaIndex, { action: newAction });
  };

  const handleActionValueChange = (field, value) => {
    updateArea(selectedAreaIndex, {
      action: { ...selectedArea.action, [field]: value }
    });
  };

  const handleBoundsChange = (field, value) => {
    const numValue = parseInt(value) || 0;
    updateArea(selectedAreaIndex, {
      bounds: { ...selectedArea.bounds, [field]: numValue }
    });
  };

  return (
    <div 
      className={cn("glass-panel rounded-lg p-4 space-y-4", className)}
      data-testid="action-editor"
    >
      <div className="flex items-center justify-between">
        <p className="label-text">Action Editor</p>
        <span className="text-xs text-[#52525B]">Area #{selectedAreaIndex + 1}</span>
      </div>

      {/* Bounds inputs */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs text-[#A1A1AA]">X</Label>
          <Input
            type="number"
            value={selectedArea.bounds.x}
            onChange={(e) => handleBoundsChange('x', e.target.value)}
            className="bg-[#18181B] border-[#27272A] font-mono text-sm"
            data-testid="bounds-x"
          />
        </div>
        <div>
          <Label className="text-xs text-[#A1A1AA]">Y</Label>
          <Input
            type="number"
            value={selectedArea.bounds.y}
            onChange={(e) => handleBoundsChange('y', e.target.value)}
            className="bg-[#18181B] border-[#27272A] font-mono text-sm"
            data-testid="bounds-y"
          />
        </div>
        <div>
          <Label className="text-xs text-[#A1A1AA]">Width</Label>
          <Input
            type="number"
            value={selectedArea.bounds.width}
            onChange={(e) => handleBoundsChange('width', e.target.value)}
            className="bg-[#18181B] border-[#27272A] font-mono text-sm"
            data-testid="bounds-width"
          />
        </div>
        <div>
          <Label className="text-xs text-[#A1A1AA]">Height</Label>
          <Input
            type="number"
            value={selectedArea.bounds.height}
            onChange={(e) => handleBoundsChange('height', e.target.value)}
            className="bg-[#18181B] border-[#27272A] font-mono text-sm"
            data-testid="bounds-height"
          />
        </div>
      </div>

      {/* Action Type */}
      <div>
        <Label className="text-xs text-[#A1A1AA] mb-2 block">Action Type</Label>
        <Select
          value={selectedArea.action.type}
          onValueChange={handleActionTypeChange}
        >
          <SelectTrigger 
            className="bg-[#18181B] border-[#27272A]"
            data-testid="action-type-select"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#18181B] border-[#27272A]">
            {actionTypes.map((type) => {
              const Icon = type.icon;
              return (
                <SelectItem 
                  key={type.value} 
                  value={type.value}
                  className="focus:bg-[#27272A]"
                >
                  <div className="flex items-center gap-2">
                    <Icon className="w-4 h-4" />
                    <span>{type.label}</span>
                  </div>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>

      {/* Action Value */}
      {selectedArea.action.type === 'message' && (
        <div>
          <Label className="text-xs text-[#A1A1AA] mb-2 block">Message Text</Label>
          <Input
            value={selectedArea.action.text || ''}
            onChange={(e) => handleActionValueChange('text', e.target.value)}
            placeholder="Enter message text"
            className="bg-[#18181B] border-[#27272A]"
            data-testid="action-message-text"
          />
        </div>
      )}

      {selectedArea.action.type === 'uri' && (
        <div>
          <Label className="text-xs text-[#A1A1AA] mb-2 block">URL</Label>
          <Input
            value={selectedArea.action.uri || ''}
            onChange={(e) => handleActionValueChange('uri', e.target.value)}
            placeholder="https://example.com"
            className="bg-[#18181B] border-[#27272A]"
            data-testid="action-uri"
          />
        </div>
      )}

      {selectedArea.action.type === 'richmenuswitch' && (
        <>
          <div>
            <Label className="text-xs text-[#A1A1AA] mb-2 block">Rich Menu Alias ID</Label>
            <Input
              value={selectedArea.action.richMenuAliasId || ''}
              onChange={(e) => handleActionValueChange('richMenuAliasId', e.target.value)}
              placeholder="richmenu-alias-xxx"
              className="bg-[#18181B] border-[#27272A]"
              data-testid="action-alias-id"
            />
          </div>
          <div>
            <Label className="text-xs text-[#A1A1AA] mb-2 block">Data (optional)</Label>
            <Input
              value={selectedArea.action.data || ''}
              onChange={(e) => handleActionValueChange('data', e.target.value)}
              placeholder="Custom data"
              className="bg-[#18181B] border-[#27272A]"
              data-testid="action-data"
            />
          </div>
        </>
      )}

      {/* Delete button */}
      <Button
        variant="destructive"
        size="sm"
        onClick={() => removeArea(selectedAreaIndex)}
        className="w-full mt-4"
        data-testid="delete-area-btn"
      >
        <Trash2 className="w-4 h-4 mr-2" />
        Delete Area
      </Button>
    </div>
  );
};

export default ActionEditor;
